import moment from "moment";
import React, { useMemo, useState } from "react";
import { Badge, Button, Pagination, Table } from "flowbite-react";
import { useQuery, useQueryClient } from "react-query";
import { IoMdAdd, IoMdSearch, IoMdSwap } from "react-icons/io";
import { FaRegEdit } from "react-icons/fa";
import { MdDeleteOutline } from "react-icons/md";
import { HiOutlineViewColumns } from "react-icons/hi2";
import { Tooltip } from "react-tooltip";
import axios from "../../config/axiosInstance";
import { CATEGORIES, LEADS } from "../../config/apiConfig";
import { formatDocument } from "../../utils/utils";
import EmptyTable from "./EmptyTable";
import ModalDelete from "../modals/ModalDelete";
import QuickCreateCustomer from "../forms/QuickCreateCustomer";

const STATUS_FILTERS = [
  { key: "all", label: "Todos" },
  { key: "1", label: "Ativos" },
  { key: "0", label: "Bloqueados" },
  { key: "2", label: "Inadimplentes" },
];

const CUSTOMER_TYPE_FILTERS = [
  { key: "all", label: "Todos" },
  { key: "lead", label: "Somente leads" },
  { key: "customer", label: "Somente clientes" },
];

const getCustomerName = (customer) =>
  customer?.name || customer?.socialReason || "Sem nome";

const getCustomerDocument = (customer) => formatDocument(customer?.document || "");

const getCustomerState = (customer) => {
  const state = String(customer?.state || customer?.uf || "")
    .trim()
    .toUpperCase();
  return state || "-";
};

const getIsCustomer = (customer) => {
  const raw = customer?.isCustomer;
  if (typeof raw === "boolean") return raw;
  if (typeof raw === "number") return raw === 1;
  if (typeof raw === "string") {
    const value = raw.trim().toLowerCase();
    return value === "true" || value === "1";
  }
  return false;
};

const uniqueList = (list = []) => {
  const seen = new Set();
  const result = [];

  list.forEach((item) => {
    const value = String(item || "").trim();
    if (!value) return;
    const key = value.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    result.push(value);
  });

  return result;
};

const getCustomerFunnelSummary = (customer) => {
  if (customer?.funnelDisplay) {
    return customer.funnelDisplay;
  }

  const directNames = uniqueList([
    customer?.funnelName,
    customer?.funnel?.name,
    customer?.Funnel?.name,
    customer?.stage?.funnelName,
    customer?.stage?.funnel?.name,
    customer?.convertedCard?.funnelName,
    customer?.convertedCard?.funnel?.name,
    customer?.convertedCard?.stage?.funnelName,
    customer?.convertedCard?.stage?.funnel?.name,
  ]);

  const funnelListNames = uniqueList([
    ...(Array.isArray(customer?.funnels)
      ? customer.funnels.map((funnel) => funnel?.name || funnel)
      : []),
    ...(Array.isArray(customer?.Funnel)
      ? customer.Funnel.map((funnel) => funnel?.name || funnel)
      : []),
  ]);

  const cardFunnels = Array.isArray(customer?.cards)
    ? customer.cards
        .map((card) => ({
          name:
            card?.funnelName ||
            card?.funnel?.name ||
            card?.stage?.funnelName ||
            card?.stage?.funnel?.name ||
            null,
          time:
            new Date(card?.updatedAt || card?.createdAt || 0).getTime() || 0,
        }))
        .filter((entry) => entry.name)
        .sort((a, b) => a.time - b.time)
        .map((entry) => entry.name)
    : [];

  const orderedFunnels = uniqueList([
    ...funnelListNames,
    ...cardFunnels,
    ...directNames,
  ]);

  if (!orderedFunnels.length) {
    return "-";
  }

  if (orderedFunnels.length === 1) {
    return orderedFunnels[0];
  }

  const lastFunnel = orderedFunnels[orderedFunnels.length - 1];
  const remaining = orderedFunnels.length - 1;
  return `${lastFunnel} +${remaining}`;
};

const parseFunnelDisplay = (rawValue) => {
  const value = String(rawValue || "").trim();
  if (!value || value === "-") {
    return { main: "Sem funil", extraCount: 0, isEmpty: true };
  }

  const match = value.match(/^(.*)\s\+(\d+)$/);
  if (match) {
    return {
      main: (match[1] || "").trim() || "Funil",
      extraCount: Number(match[2] || 0),
      isEmpty: false,
    };
  }

  return {
    main: value,
    extraCount: 0,
    isEmpty: false,
  };
};

const AdminLeadsTable = () => {
  const queryClient = useQueryClient();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [searchName, setSearchName] = useState("");
  const [nameParam, setNameParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [customerTypeFilter, setCustomerTypeFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isOpenModalDelete, setOpenModalDelete] = useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);

  const [isOpenQuickCreate, setIsOpenQuickCreate] = useState(false);
  const [isDrawerEditCustomer, setIsDrawerEditCustomer] = useState(false);
  const [convertingLeadId, setConvertingLeadId] = useState(null);
  const [isOpenModalConvert, setOpenModalConvert] = useState(false);
  const [selectedLeadToConvert, setSelectedLeadToConvert] = useState(null);

  const {
    isLoading: isLoadingListCustomers,
    error: errorListCustomers,
    data: listCustomers,
  } = useQuery(
    ["listCustomers", currentPage, nameParam, customerTypeFilter],
    async () => {
      const params = new URLSearchParams({
        page: String(currentPage),
        search: nameParam,
      });

      if (customerTypeFilter === "lead") {
        params.set("isCustomer", "false");
      }

      if (customerTypeFilter === "customer") {
        params.set("isCustomer", "true");
      }

      const { data } = await axios.get(`${LEADS.LIST}?${params.toString()}`);

      setTotalPages(data?.data?.totalPages || 1);
      setTotalCount(data?.data?.totalCount || 0);
      return data?.data?.data || [];
    }
  );

  useQuery(["categories"], async () => {
    const { data } = await axios.get(
      `${CATEGORIES.GET_ALL}?itemsPerPage=${-1}`
    );
    return data?.data?.data || [];
  });

  const statusCounts = useMemo(() => {
    const source = listCustomers || [];
    return source.reduce(
      (acc, customer) => {
        const key = String(customer?.status);
        if (!acc[key]) {
          acc[key] = 0;
        }
        acc[key] += 1;
        acc.all += 1;
        return acc;
      },
      { all: 0, "1": 0, "0": 0, "2": 0 }
    );
  }, [listCustomers]);

  const stateOptions = useMemo(() => {
    const source = listCustomers || [];
    const states = uniqueList(
      source
        .map((customer) => getCustomerState(customer))
        .filter((state) => state && state !== "-")
    ).sort((a, b) => a.localeCompare(b, "pt-BR"));

    return [{ key: "all", label: "Todos os estados" }].concat(
      states.map((state) => ({ key: state, label: state }))
    );
  }, [listCustomers]);

  const filteredCustomers = useMemo(() => {
    if (!listCustomers) return [];
    return listCustomers.filter((customer) => {
      const matchesStatus =
        statusFilter === "all" || String(customer.status) === statusFilter;
      const state = getCustomerState(customer);
      const matchesState = stateFilter === "all" || state === stateFilter;
      return matchesStatus && matchesState;
    });
  }, [listCustomers, statusFilter, stateFilter]);

  const summary = useMemo(() => {
    const totalVisible = filteredCustomers.length;
    const blockedVisible = filteredCustomers.filter((c) => c?.status === 0).length;
    const activeVisible = filteredCustomers.filter((c) => c?.status === 1).length;
    return {
      totalVisible,
      blockedVisible,
      activeVisible,
    };
  }, [filteredCustomers]);

  const handleSearchName = (event) => {
    event.preventDefault();
    setNameParam(searchName.trim());
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchName("");
    setNameParam("");
    setStatusFilter("all");
    setCustomerTypeFilter("all");
    setStateFilter("all");
    setCurrentPage(1);
  };

  const handleEditCustomer = (company) => {
    setSelectedCompany(company);
    setIsDrawerEditCustomer(true);
  };

  const handleOpenConvertLeadModal = (customer) => {
    setSelectedLeadToConvert(customer);
    setOpenModalConvert(true);
  };

  const handleToggleCustomerType = async () => {
    if (!selectedLeadToConvert?.id) return;

    try {
      setConvertingLeadId(selectedLeadToConvert.id);
      const isCustomer = getIsCustomer(selectedLeadToConvert);
      await axios.put(LEADS.UPDATE(selectedLeadToConvert.id), {
        isCustomer: !isCustomer,
      });

      queryClient.invalidateQueries("listCustomers");
      setOpenModalConvert(false);
      setSelectedLeadToConvert(null);
    } catch (error) {
      console.error("Erro ao alterar tipo de cadastro:", error);
    } finally {
      setConvertingLeadId(null);
    }
  };

  const handleOpenDeleteCustomerModal = (company) => {
    setSelectedCompany(company);
    setOpenModalDelete(true);
  };

  const handleDeleteCustomer = async () => {
    if (!selectedCompany?.id) return;

    try {
      setIsLoadingDelete(true);
      await axios.delete(LEADS.DELETE(selectedCompany.id));
      queryClient.invalidateQueries("listCustomers");
      setOpenModalDelete(false);
      setSelectedCompany(null);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingDelete(false);
    }
  };

  const renderStateBadge = (customer) => {
    const state = getCustomerState(customer);

    return (
      <Badge color={state === "-" ? "gray" : "light"} className="w-fit text-[11px]">
        {state}
      </Badge>
    );
  };

  const renderCustomerTypeBadge = (customer) => {
    const isCustomer = getIsCustomer(customer);

    return (
      <Badge
        color={isCustomer ? "success" : "gray"}
        className="w-fit text-[11px]"
      >
        {isCustomer ? "Sim" : "Não"}
      </Badge>
    );
  };

  const renderActionButtons = (customer) => {
    const isCustomer = getIsCustomer(customer);

    return (
      <div className="flex items-center justify-end gap-2">
        <button
          data-tooltip-id="tt-convert-customer"
          onClick={() => handleOpenConvertLeadModal(customer)}
          disabled={convertingLeadId === customer?.id}
          className="rounded-lg border border-sky-200 bg-white p-2 text-sky-600 transition hover:bg-sky-50 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label={
            isCustomer ? "Converter cliente para lead" : "Converter lead para cliente"
          }
        >
          <IoMdSwap size={17} />
        </button>

        <button
          data-tooltip-id="tt-edit-customer"
          onClick={() => handleEditCustomer(customer)}
          className="rounded-lg border border-gray-200 bg-white p-2 text-gray-600 transition hover:bg-gray-100 hover:text-gray-900"
          aria-label="Editar cliente"
        >
          <FaRegEdit size={15} />
        </button>

        <button
          data-tooltip-id="tt-delete-customer"
          onClick={() => handleOpenDeleteCustomerModal(customer)}
          className="rounded-lg border border-rose-200 bg-white p-2 text-rose-600 transition hover:bg-rose-50 hover:text-rose-700"
          aria-label="Excluir cliente"
        >
          <MdDeleteOutline size={15} />
        </button>
      </div>
    );
  };

  return (
    <>
      <Tooltip id="tt-convert-customer" content="Alternar lead/cliente" />
      <Tooltip id="tt-edit-customer" content="Editar lead" />
      <Tooltip id="tt-delete-customer" content="Excluir cliente" />

      <section className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-6 text-white shadow-lg">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.35em] text-white/70">
              Cadastro
            </p>
            <h1 className="mt-2 text-2xl font-bold tracking-tight">Gestão de Clientes</h1>
            <p className="mt-1 text-sm text-white/80">
              Visualize, atualize e administre os clientes e seus status operacionais.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="rounded-xl border border-white/20 bg-white/10 px-3 py-2 text-sm">
              Total geral: <span className="font-semibold">{totalCount}</span>
            </div>
            <Button
              className="bg-white !text-slate-900 hover:!bg-slate-100"
              onClick={() => setIsOpenQuickCreate(true)}
            >
              <IoMdAdd className="mr-1" size={18} />
              Novo cliente
            </Button>
          </div>
        </div>
      </section>

      <section className="mt-4 rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
        <form
          onSubmit={handleSearchName}
          className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_220px_180px_auto_auto]"
        >
          <div className="relative">
            <IoMdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Buscar por nome, documento, e-mail ou telefone"
              className="w-full rounded-xl border border-gray-200 py-2.5 pl-10 pr-3 text-sm focus:border-slate-400 focus:ring-0"
              onChange={(event) => setSearchName(event.target.value)}
              value={searchName}
            />
          </div>

          <select
            className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-slate-400 focus:ring-0"
            value={customerTypeFilter}
            onChange={(event) => {
              setCustomerTypeFilter(event.target.value);
              setCurrentPage(1);
            }}
          >
            {CUSTOMER_TYPE_FILTERS.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-slate-400 focus:ring-0"
            value={stateFilter}
            onChange={(event) => {
              setStateFilter(event.target.value);
              setCurrentPage(1);
            }}
          >
            {stateOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>

          <Button type="submit" className="bg-slate-900 hover:bg-slate-800">
            Buscar
          </Button>
        </form>

        {errorListCustomers ? (
          <div className="mt-4 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            Nao foi possivel carregar os clientes. Tente novamente.
          </div>
        ) : null}
      </section>

      <section className="mt-4 rounded-3xl border border-gray-100 bg-white shadow-sm overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <Table className="text-sm">
            <Table.Head className="bg-slate-50">
              <Table.HeadCell className="text-slate-700">Cliente</Table.HeadCell>
              <Table.HeadCell className="text-slate-700">Funil</Table.HeadCell>
              <Table.HeadCell className="text-center text-slate-700">Cadastro</Table.HeadCell>
              <Table.HeadCell className="text-center text-slate-700">Estado</Table.HeadCell>
              <Table.HeadCell className="text-center text-slate-700">Cliente?</Table.HeadCell>
              <Table.HeadCell className="text-right text-slate-700">Acoes</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y divide-gray-100">
              {isLoadingListCustomers ? (
                Array.from({ length: 8 }).map((_, index) => (
                  <Table.Row key={index} className="animate-pulse">
                    <Table.Cell>
                      <div className="h-4 w-56 rounded bg-gray-100" />
                      <div className="mt-2 h-3 w-32 rounded bg-gray-100" />
                    </Table.Cell>
                    <Table.Cell>
                      <div className="h-4 w-44 rounded bg-gray-100" />
                    </Table.Cell>
                    <Table.Cell>
                      <div className="mx-auto h-4 w-24 rounded bg-gray-100" />
                    </Table.Cell>
                    <Table.Cell>
                      <div className="mx-auto h-5 w-20 rounded-full bg-gray-100" />
                    </Table.Cell>
                    <Table.Cell>
                      <div className="mx-auto h-5 w-14 rounded-full bg-gray-100" />
                    </Table.Cell>
                    <Table.Cell>
                      <div className="ml-auto h-8 w-28 rounded bg-gray-100" />
                    </Table.Cell>
                  </Table.Row>
                ))
              ) : filteredCustomers.length ? (
                filteredCustomers.map((customer) => (
                  <Table.Row key={customer.id} className="hover:bg-slate-50/70">
                    <Table.Cell>
                      <div className="font-semibold text-gray-900">{getCustomerName(customer)}</div>
                      <div className="mt-1 text-xs text-gray-500">
                        Documento: {getCustomerDocument(customer)}
                      </div>
                    </Table.Cell>

                    <Table.Cell>
                      <FunnelBadge value={getCustomerFunnelSummary(customer)} />
                    </Table.Cell>

                    <Table.Cell className="text-center">
                      <div className="text-gray-900">
                        {moment(customer.createdAt).format("DD/MM/YYYY")}
                      </div>
                      <div className="text-xs text-gray-500">
                        {moment(customer.createdAt).fromNow()}
                      </div>
                    </Table.Cell>

                    <Table.Cell>
                      <div className="mx-auto w-fit">{renderStateBadge(customer)}</div>
                    </Table.Cell>

                    <Table.Cell>
                      <div className="mx-auto w-fit">
                        {renderCustomerTypeBadge(customer)}
                      </div>
                    </Table.Cell>

                    <Table.Cell>{renderActionButtons(customer)}</Table.Cell>
                  </Table.Row>
                ))
              ) : (
                <Table.Row>
                  <Table.Cell colSpan={6} className="py-10">
                    <EmptyTable icon="person_off">Nenhum lead encontrado</EmptyTable>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>

        <div className="grid grid-cols-1 gap-3 p-3 md:hidden">
          {isLoadingListCustomers ? (
            Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="animate-pulse rounded-2xl border border-gray-200 bg-white p-4"
              >
                <div className="h-4 w-2/3 rounded bg-gray-100" />
                <div className="mt-2 h-3 w-1/2 rounded bg-gray-100" />
                <div className="mt-4 h-8 w-full rounded bg-gray-100" />
              </div>
            ))
          ) : filteredCustomers.length ? (
            filteredCustomers.map((customer) => (
              <article
                key={customer.id}
                className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="truncate text-sm font-semibold text-gray-900">
                      {getCustomerName(customer)}
                    </h3>
                    <p className="mt-1 text-xs text-gray-500">
                      Documento: {getCustomerDocument(customer)}
                    </p>
                  </div>
                  {renderStateBadge(customer)}
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <InfoTile
                    label="Cadastro"
                    value={moment(customer.createdAt).format("DD/MM/YYYY")}
                    subvalue={moment(customer.createdAt).fromNow()}
                  />
                  <InfoTile
                    label="Funil"
                    value={<FunnelBadge value={getCustomerFunnelSummary(customer)} compact />}
                  />
                  <InfoTile
                    label="E cliente"
                    value={getIsCustomer(customer) ? "Sim" : "Nao"}
                  />
                </div>

                <div className="mt-3">{renderActionButtons(customer)}</div>
              </article>
            ))
          ) : (
            <EmptyTable icon="person_off">Nenhum lead encontrado</EmptyTable>
          )}
        </div>
      </section>

      {totalCount > 0 ? (
        <section className="mt-4 flex flex-col items-center gap-3 pb-2">
          <p className="text-sm text-gray-600">
            Foram encontrados <span className="font-semibold">{totalCount}</span> registros.
          </p>

          {totalPages > 1 ? (
            <Pagination
              layout="pagination"
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              previousLabel=""
              nextLabel=""
              showIcons
            />
          ) : null}
        </section>
      ) : null}

      <QuickCreateCustomer
        show={isOpenQuickCreate}
        onClose={() => setIsOpenQuickCreate(false)}
        onUpdated={() => {
          queryClient.invalidateQueries(["listCustomers", currentPage, nameParam]);
        }}
      />

      <QuickCreateCustomer
        show={isDrawerEditCustomer}
        onClose={() => setIsDrawerEditCustomer(false)}
        mode="edit"
        initialCompany={selectedCompany}
        onUpdated={() => {
          queryClient.invalidateQueries(["listCustomers", currentPage, nameParam]);
        }}
      />

      <ModalDelete
        title={`Deseja excluir o cliente ${
          selectedCompany?.name || selectedCompany?.companyName || "selecionado"
        }?`}
        isOpen={isOpenModalDelete}
        setOpenModal={setOpenModalDelete}
        isLoading={isLoadingDelete}
        handleDelete={handleDeleteCustomer}
      />

      <ModalDelete
        title={`Deseja converter ${
          selectedLeadToConvert?.name || selectedLeadToConvert?.companyName || "selecionado"
        } para ${getIsCustomer(selectedLeadToConvert) ? "lead" : "cliente"}?`}
        isOpen={isOpenModalConvert}
        setOpenModal={setOpenModalConvert}
        isLoading={convertingLeadId === selectedLeadToConvert?.id}
        handleDelete={handleToggleCustomerType}
      />
    </>
  );
};

function SummaryCard({ label, value, tone }) {
  const toneClass =
    tone === "emerald"
      ? "text-emerald-700 bg-emerald-50 border-emerald-100"
      : tone === "rose"
      ? "text-rose-700 bg-rose-50 border-rose-100"
      : "text-slate-700 bg-slate-50 border-slate-100";

  return (
    <div className={`rounded-2xl border p-3 ${toneClass}`}>
      <p className="text-[11px] uppercase tracking-[0.3em]">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{value}</p>
    </div>
  );
}

function InfoTile({ label, value, subvalue }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50 p-2.5">
      <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500">{label}</p>
      {typeof value === "string" || typeof value === "number" ? (
        <p className="mt-1 truncate font-medium text-gray-800" title={value}>
          {value || "-"}
        </p>
      ) : (
        <div className="mt-1">{value}</div>
      )}
      {subvalue ? <p className="text-[11px] text-gray-500">{subvalue}</p> : null}
    </div>
  );
}

function FunnelBadge({ value, compact = false }) {
  const { main, extraCount, isEmpty } = parseFunnelDisplay(value);

  return (
    <div
      className={`inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 ${
        isEmpty
          ? "border-gray-200 bg-gray-50 text-gray-500"
          : "border-indigo-100 bg-indigo-50 text-indigo-700"
      } ${compact ? "text-[11px]" : "text-xs"}`}
      title={String(value || "-")}
    >
      <HiOutlineViewColumns className={`${compact ? "text-[12px]" : "text-[13px]"}`} />
      <span className="truncate max-w-[150px] font-medium">{main}</span>
      {extraCount > 0 ? (
        <span className="inline-flex items-center rounded-full bg-indigo-100 px-1.5 py-[1px] text-[10px] font-semibold text-indigo-700">
          +{extraCount}
        </span>
      ) : null}
    </div>
  );
}

export default AdminLeadsTable;
